import { atom, computed, map } from 'nanostores';

import type { Message, ParticipantStatus, SyncMode } from './sync_client';

/**
 * Manages participant statuses and session sync state
 */
export class ParticipantManager {
  #sendMessage: (message: Message) => void;

  constructor(sendMessage: (message: Message) => void) {
    this.#sendMessage = sendMessage;
  }

  $participantStatuses = map<Record<string, ParticipantStatus>>({});

  $sessionSyncMode = computed(
    [this.$participantStatuses],
    (participantStatuses) => {
      if (Object.keys(participantStatuses).length === 0) {
        return 'OFFLINE';
      }

      if (
        Object.values(participantStatuses).some(
          (status) => status.status === 'PRESENTATION',
        )
      ) {
        return 'PRESENTATION';
      }

      if (
        Object.values(participantStatuses).some(
          (status) => status.status === 'SYNC',
        )
      ) {
        return 'SYNC';
      }
      return 'ONLINE';
    },
  );

  $userSyncMode = atom<SyncMode>('OFFLINE');
  $syncActive = computed(
    [this.$userSyncMode, this.$sessionSyncMode],
    (userSyncMode, sessionSyncMode) => {
      if (
        userSyncMode === 'OFFLINE' ||
        userSyncMode === 'ONLINE' ||
        (userSyncMode === 'SYNC' && sessionSyncMode === 'PRESENTATION')
      ) {
        return false;
      }
      return true;
    },
  );

  setSyncMode(mode: SyncMode): void {
    const currentSyncMode = this.$userSyncMode.get();

    this.#sendMessage({
      command: 'SET_MODE',
      data: {
        mode,
      },
    });

    if (currentSyncMode === mode) {
      return;
    }
    if (currentSyncMode !== 'PRESENTATION' && mode === 'SYNC') {
      this.#sendMessage({
        command: 'BOOTSTRAP',
      });
    }
    this.setUserSyncMode(mode);
  }

  setUserSyncMode(mode: SyncMode): void {
    this.$userSyncMode.set(mode);
  }

  updateParticipantStatus(
    clientId: string,
    userId: string,
    mode: SyncMode,
  ): void {
    if (mode === 'OFFLINE') {
      this.$participantStatuses.setKey(clientId, undefined);
    } else {
      this.$participantStatuses.setKey(clientId, {
        userId,
        status: mode,
      });
    }
  }

  clear(): void {
    this.$participantStatuses.set({});
    this.$userSyncMode.set('OFFLINE');
  }
}
